import { NgClass, NgStyle } from '@angular/common';
import { Component, OnInit, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EventType, Router } from '@angular/router';
import { KIToolsUser } from '@shared/index';
import { ConfirmModalComponent } from '@reflact/ngx-utils';
import { ExcelExportComponent, ExcelImportComponent } from '@reflact/ngx-xlsx';
import { XLSXExportConfig, XLSXImportConfig, XlsxImportResult } from 'src/app/shared/types/xlsx.types';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { PopoverModule } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { ShortyPipe } from 'src/app/shared/pipes/shorty.pipe';
import { AccesscontrolService } from 'src/app/shared/services/accesscontrol.service';
import { AdminService } from '@shared-angular/services/admin.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { LoginService } from '@shared-angular/services/login.service';
import { ProjectService } from 'src/app/shared/services/project.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { UserPermissionAdminService } from 'src/app/shared/services/user-permission-admin.service';
import { PicklistComponent } from '../../../../../../shared/components/ai-bot/utils/picklist/picklist.component';
import { AddModalComponent } from '../../../admin-pages/projects-page/projectmanagement-page/add-modal/add-modal.component';
@Component({
  imports: [NgClass, NgStyle, 
    FormsModule,
    //HintComponent,
    BsDropdownModule,
    ShortyPipe,
    //TemplateAndDemoShareComponentComponent,
    ExcelExportComponent,
    ExcelImportComponent,
    PicklistComponent,
    ButtonModule,
    InputTextModule,
    Textarea,
    CardModule,
    CheckboxModule,
    MenuModule,
    PopoverModule,
    MessageModule,
    TableModule
  ],
  providers: [BsModalService],
  templateUrl: './access-control-tab.component.html',
  styleUrl: './access-control-tab.component.scss'
})
export class AccessControlTabComponent implements OnInit {
  protected deleteLabel = $localize`:@@delete:Löschen`;

  public excelImportConf: XLSXImportConfig[] = [
    { fieldName: "guestId", type: "string", required: true, label: "access" },
    { fieldName: "metadata", type: "string", required: true }
  ];

  public excelExportConf: XLSXExportConfig[] = [
    { fieldName: "guestId", label: "access" },
    { fieldName: "metadata" },
    { fieldName: "created" },
    { fieldName: "updated" },
    { fieldName: "chat" },
    { fieldName: "audio" },
    { fieldName: "avatar" }
  ];

  public excelStatsExportConf: XLSXExportConfig[] = [
    { fieldName: "guestId", label: "access" },
    { fieldName: "chat" },
    { fieldName: "audio" },
    { fieldName: "avatar" }
  ];

  public modalRef?: BsModalRef;
  public isUserAddingLoading: boolean = false;
  private readonly userActionMenuItems = new Map<string, MenuItem[]>();

  constructor(
    protected accessService: AccesscontrolService,
    protected projectService: ProjectService,
    protected permissionService: UserPermissionAdminService,
    public rss: RouteShareService,
    public loginService: LoginService,
    public toastr: ToastrService,
    public adminService: AdminService,
    private modalService: BsModalService,
    private router: Router,
    private languageService: LanguageService
  ) {
    this.router.events.subscribe(event => {
      if (event.type == EventType.NavigationEnd) {
        this.accessService.searchString = "";
        this.accessService.selectedUserIds = [];
        this.accessService.allSelected = false;
      }
    });
  }

  public ngOnInit(): void {
    if (this.projectService.selectedProject) {
      this.permissionService.selectedProjectId = this.projectService.selectedProject._id;
    } else {
      this.permissionService.selectedProjectId = "";
    }
    if (this.rss.botConfig._id) {
      this.permissionService.selectedBotId = this.rss.botConfig._id;
    } else {
      this.permissionService.selectedBotId = "";
    }
  }

  public async importUsers(res: XlsxImportResult<{ guestId: string, metadata: string }>) {
    if (res.status == "success") {
      await this.addToUsersList(res.data);
    }
  }

  protected async addToUsersList(users: { guestId: string, metadata: string }[] = [{ guestId: "", metadata: "" }]) {
    if (this.isUserAddingLoading) return;

    if (this.accessService.kiToolUserList.some(user => users.find(u => user.guestId == u.guestId))) {
      const modal = this.modalService.show(ConfirmModalComponent);
      if (modal.content && modal.onHide) {
        modal.content.headerText = $localize`:@@duplicateUserFound:Doppelte Nutzer erkannt!`;
        modal.content.bodyText = $localize`:@@confirmOverrideUser:Durch das Importieren aus einem anderen Bot werden die Metadaten aller Nutzer überschrieben, die bereits in der Liste vorkommen. Möchtest du fortfahren?`;
        modal.content.acceptButtonText = $localize`:@@yes:Ja`;
        modal.content.denyButtonText = $localize`:@@no:Nein`;
        modal.onHide.subscribe(async () => {
          if (!modal.content?.accepted) return;
          this.isUserAddingLoading = true;
          for (const user of users) {
            try {
              await this.accessService.addUserToList(user.guestId, user.metadata);
            } catch (e) { console.error(e); }
          }
          this.isUserAddingLoading = false;
        });
      }
      return;
    }
    this.isUserAddingLoading = true;
    for (const user of users) {
      try {
        await this.accessService.addUserToList(user.guestId, user.metadata);
      } catch (e) { console.error(e); }
    }
    this.isUserAddingLoading = false;
  }

  public setInitialPwWhenEmpty() {
    if (this.rss.botConfig.botPassword.length == 0) {
      this.rss.botConfig.botPassword = this.getRandomPW();
      void this.rss.saveBotConfigField("botPassword");
    }
  }

  public getRandomPW() {
    const length = 8;
    const charset = "abcdefghijklmnopqrstuvwxyz0123456789";
    let newPw = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      newPw += charset.charAt(Math.floor(Math.random() * n));
    }
    return newPw;
  }

  public async resetUserStats(userId: string) {
    await this.accessService.resetUserStats(userId);
  }

  protected selectedIncludesAllShown() {
    if (this.accessService.filteredUsers.length == 0) { return false; }
    return this.accessService.filteredUsers.every(u => this.accessService.selectedUserIds.includes(u._id));
  }

  protected changeAllSelected(select: boolean) {
    if (select) {
      this.accessService.selectedUserIds.push(...this.accessService.filteredUsers.filter(u => !this.accessService.selectedUserIds.includes(u._id)).map(u => u._id));
    } else {
      this.accessService.selectedUserIds = [];
    }
  }

  protected changeSelection(id: string) {
    if (this.accessService.selectedUserIds.includes(id)) {
      this.accessService.selectedUserIds = this.accessService.selectedUserIds.filter(s => s != id);
    } else {
      this.accessService.selectedUserIds.push(id);
    }
    this.accessService.allSelected = this.accessService.selectedUserIds.length == this.accessService.kiToolUserList.length;
  }



  protected confirmDelete() {
    const modal = this.modalService.show(ConfirmModalComponent);
    if (modal.content && modal.onHide) {
      modal.content.bodyText = $localize`:@@confirm-removal-user-list:Möchtest du wirklich ${this.accessService.selectedUserIds.length} Nutzer von der Liste entfernen?`;
      modal.content.acceptButtonText = $localize`:@@yes:Ja`;
      modal.content.denyButtonText = $localize`:@@no:Nein`;
      modal.onHide.subscribe(async () => {
        if (!modal.content?.accepted) return;
        await this.accessService.deleteSelected();
      });
    }
  }

  public confirmOpenLink(tpl: TemplateRef<BsModalRef>) {
    this.modalRef = this.modalService.show(tpl);
  }

  public getLink(userId: string, type: "chat" | "audio" | "video" | "token") {

    const user = this.accessService.kiToolUserList.find((entry: KIToolsUser) => entry._id == userId);
    let link = "";
    if (user) {
      link = window.location.origin + '/' + this.languageService.getLanguage() + "/botaccess?accesstoken=" + user.accessToken;
      if (type === "audio") {
        link += "&target=audio";
      } else if (type === "video") {
        link += "&target=avatar";
      }
    }
    return link;
  }

  public copyLink(link: string) {
    void window.navigator.clipboard.writeText(link);
    this.toastr.success("GENERAL", $localize`:@@copied-link-to-clip:Link kopiert`);
  }

  public guestIdAlreadyInList(userId: string): boolean {
    return this.accessService.kiToolUserList.filter(u => u.guestId == userId).length > 1;
  }

  protected getUserActionMenuItems(userId: string): MenuItem[] {
    const existingItems = this.userActionMenuItems.get(userId);
    if (existingItems) {
      return existingItems;
    }

    const items: MenuItem[] = [
      {
        label: $localize`:@@reset-statistic:Statistik zurücksetzen`,
        icon: 'ri-reset-left-fill',
        command: () => {
          void this.resetUserStats(userId);
        }
      },
      {
        label: $localize`:@@delete:Löschen`,
        icon: 'ri-close-large-fill',
        command: () => {
          this.accessService.selectedUserIds = [userId];
          this.confirmDelete();
        },
        styleClass: 'text-danger'
      }
    ];

    this.userActionMenuItems.set(userId, items);
    return items;
  }

  protected openAddModal() {
    this.modalService.show(AddModalComponent, {
      class: 'modal-lg',
      keyboard: false,
      ignoreBackdropClick: true,
      initialState: {
        type: 'user'
      }
    });
  }
}
